import React from 'react';
import Product from '../components/Product';
import Variants from '../components/CheckoutForm/components/Variants';
import CheckoutForm from '../components/CheckoutForm';
import useCheckoutState from '../hooks/useCheckoutState';
import clsx from 'clsx';
import CheckoutConfirmation from '../CheckoutConfirmation';
import CheckoutFormContainer from '../components/CheckoutFormContainer';
import Button from '../components/Button';
import PoweredByStripe from '../components/PoweredByStripe/PoweredByStripe';
import Media from '../components/Media';
import { StoreNameWithLabel } from '../components/Product/Product';
import { PAGE_TYPES } from '../enums/page-types';
import { getMedia } from '../lib/getMedia';
import EventDetails from '../components/EventDetails';
import { checkHasEventModules, checkHasProductModules } from '../enums/modules';
import useCartCost from '../hooks/useCartCost';
import TicketGroups from '../components/TicketGroups';

export default function CheckoutDouble() {
  const { checkout, seller, query, isUpsell, product, isScreenshot, isConfirmation } =
    useCheckoutState();
  const { productPriceAmount, setupFeeAmount } = useCartCost();

  const hasTicketTypes = checkout.type === PAGE_TYPES.EVENT;
  const hasVariants = product?.variants?.length > 0;

  const hasProductModules = checkHasProductModules({
    checkout,
    seller,
    query,
    isScreenshot,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: product.discountedFromPrice,
    trialPeriodDays: product.trialPeriodDays,
    startDate: product.startDate,
  });

  const hasEventModules = checkHasEventModules({
    checkout,
    seller,
  });

  const media = getMedia({ checkout, product });
  const hasMedia = media && media.length > 0;

  return (
    <div
      className={clsx(
        checkout.backgroundWidth === 'half' && [
          checkout.formPosition === 'right' ? 'md:bg-gradient-to-r' : 'md:bg-gradient-to-l',
          'from-[var(--checkout-bg)] from-50% to-white to-50%',
        ],
      )}
      style={{
        '--checkout-bg': checkout.backgroundColor,
        background: checkout.backgroundWidth === 'full' ? checkout.backgroundColor : undefined,
      }}
    >
      <div className="mx-auto max-w-screen-xl">
        <div className="cp_checkout cp_checkout-double md:grid md:grid-cols-2 min-h-screen overflow-auto">
          <div
            className={clsx(
              'bg-[var(--checkout-bg)]',
              checkout.formPosition === 'left' ? 'order-2' : 'order-1',
              checkout.backgroundWidth === 'half' && 'md:bg-transparent',
            )}
          >
            <div className={clsx('cp_card mx-auto max-w-screen-xs px-4 pb-8 pt-12 md:pt-20')}>
              <StoreNameWithLabel />

              {hasMedia && (
                <div className={clsx('cp_product-image-wrapper space-y-4 mb-4')}>
                  <Media media={media} />
                </div>
              )}

              {hasEventModules && <EventDetails />}

              {hasProductModules && <Product />}
            </div>
          </div>

          <div
            className={clsx(
              checkout.backgroundWidth === 'half' ? 'bg-white' : 'bg-transparent',
              checkout.formPosition === 'left' ? 'order-1' : 'order-2',
            )}
          >
            {isConfirmation && (
              <div
                className={clsx('cp_card mx-auto max-w-screen-xs', {
                  'bg-white md:bg-transparent md:my-20': checkout.backgroundWidth === 'full',
                })}
              >
                <div
                  className={clsx('cp_card bg-white', {
                    'pt-8 md:pt-4 px-4 sm:mx-4 md:rounded max-w-screen-xs mx-auto':
                      checkout.backgroundWidth === 'full',
                    'pt-8 px-4 pb-12 md:pt-20': checkout.backgroundWidth === 'half',
                  })}
                >
                  <CheckoutConfirmation />
                </div>
              </div>
            )}

            {!isConfirmation && (
              <div
                className={clsx('cp_card mx-auto max-w-screen-xs', {
                  'bg-white md:bg-transparent md:my-20': checkout.backgroundWidth === 'full',
                })}
              >
                <div
                  className={clsx('cp_card bg-white', {
                    'pt-8 md:pt-4 px-4 sm:mx-4 md:rounded max-w-screen-xs mx-auto':
                      checkout.backgroundWidth === 'full',
                    'pt-8 px-4 pb-12 md:pt-20': checkout.backgroundWidth === 'half',
                  })}
                >
                  <CheckoutFormContainer>
                    {checkout.type === PAGE_TYPES.CHECKOUT &&
                      checkout.showPaymentDetailsText &&
                      !isUpsell && (
                        <h3 className="mb-4 font-medium">{checkout.paymentDetailsText}</h3>
                      )}

                    {checkout.type === PAGE_TYPES.FORM && checkout.showFormDetailsText && (
                      <h2 className="mb-4 font-medium">{checkout.formDetailsText}</h2>
                    )}

                    {hasTicketTypes && (
                      <div className="cp_ticket-types-wrapper -mx-4 mb-4">
                        <TicketGroups />
                      </div>
                    )}

                    {hasVariants && (
                      <div className="cp_variants-wrapper -mx-4 mb-4">
                        <Variants />
                      </div>
                    )}

                    <CheckoutForm />
                  </CheckoutFormContainer>

                  {(checkout.type === PAGE_TYPES.CHECKOUT || checkout.type === PAGE_TYPES.EVENT) &&
                    checkout.showPoweredByStripe && (
                      <div className="cp_powered-by-stripe-wrapper w-full flex justify-center">
                        <Button
                          to="https://stripe.com"
                          target="_blank"
                          appearance="bare"
                          className="cp_powered-by-stripe-link"
                        >
                          <PoweredByStripe style={checkout.poweredByStripeStyle} />
                        </Button>
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
